import { useStyles } from '@archipro-design/aria';
import * as S from './RefundPolicy.style';

interface ReturnsPolicyTemplateProps {
    data: {
        Professional: {
            Title: string;
            Link: string;
        };
    };
}

const ReturnsPolicyTemplate = ({
    data: { Professional },
}: ReturnsPolicyTemplateProps) => {
    const { css } = useStyles();
    const contactURL = `${Professional?.Link}/contact`;

    return (
        <div className={css(S.ReturnTemplate)}>
            <strong className={css(S.PolicyNotice)}>
                Please note, your transaction is between yourself (the
                purchaser), and the seller of the product (the merchant).
                ArchiPro does not hold any product, and does not accept product
                returns. This Returns and Refund policy is between you and the
                merchant, {Professional.Title}.
            </strong>
            <p className={css(S.BoldPolicyContent)}>Returns</p>
            <p className={css(S.BoldPolicyContent)}>Non Refundable Items</p>
            <p>Items that are not returnable include:</p>
            <ul>
                <li>
                    Products that have been purchased while on sale and priced
                    at a reduced retail price
                </li>
                <li>Bespoke and custom made products</li>
                <li>
                    Products that are indent orders or made to order where the
                    merchant orders in the product specifically for you
                </li>
                <li>Gift vouchers</li>
            </ul>
            <p>
                {Professional.Title} will accept returns for faulty or defective
                items in line with the Consumer Guarantees Act 1993 as long as
                the fault or defect is reported within 48 hours of receiving the
                item. You cannot claim if:
            </p>
            <ul>
                <li>
                    You used the product in a way that was outside the intended
                    purpose/use.
                </li>
                <li>
                    You broke or lost the product, accidental or non-accidental
                    damage.
                </li>
                <li>
                    You modified the product, and this is what caused the issue.{' '}
                </li>
                <li>
                    Normal wear and tear within the reasonable life of the
                    product.
                </li>
                <li>
                    If the fault falls outside of the standard warranty period
                    of the product.
                </li>
            </ul>
            <p className={css(S.BoldPolicyContent)}>Requesting a Return</p>
            <p>
                If your item is faulty please notify within 48 hours of
                receiving the item to arrange the return of the item.
            </p>
            <p>
                To be eligible for a return, your items must be unused, in their
                original condition and original packaging.
            </p>
            <p>
                You can request a return of your product/s by contacting us
                directly via our <a href={contactURL}>contact page</a>.
                Alternatively, you can request a refund via your ArchiPro Order
                manager:
            </p>
            <ol>
                <li>Log in to your ArchiPro account</li>
                <li>Head to the &apos;Orders&apos; tab within your profile.</li>
                <li>
                    Select the order with the product/s you would like to
                    return.
                </li>
                <li>
                    Select the items from your order that you would like to
                    return.
                    <br />
                    <strong>Note:</strong> Items can only be returned if the
                    order has been confirmed and marked as processed.
                </li>
                <li>
                    Click the &apos;Return Items&apos; button in the bottom
                    right of the &apos;order summary&apos;.
                </li>
                <li>
                    Enter a reason for returning the items, select your quantity
                    of products you wish to return, and add any relevant photos
                    you would like to include.
                </li>
                <li>
                    We&apos;ll make contact with you and organise a suitable
                    time to collect the product. Costs of shipping and or return
                    may not be covered in some cases.
                </li>
            </ol>
            <p>
                If it has a minor fault, we may choose to repair the item,
                replace it or refund your money. If it is a major fault,
                it&apos;s your choice whether you opt for a replacement or
                refund.
            </p>
            <p>
                For additional information on consumer rights and the{' '}
                <em>Consumer Guarantees Act</em>{' '}
                <a
                    href="https://www.consumerprotection.govt.nz/general-help/consumer-laws/consumer-guarantees-act"
                    target="_blank"
                    rel="noopener"
                >
                    click here
                </a>
            </p>
            <p className={css(S.BoldPolicyContent)}>Packaging Your Return</p>
            <p>
                Please do not write on the product packaging as your return
                request may be rejected and the refund declined.
            </p>
            <p>
                Ensure the item is wrapped to protect both the product and the
                packaging and the address is clearly visible.
            </p>
            <p className={css(S.BoldPolicyContent)}>Returned Items Process</p>
            <p>
                Once we receive your item, we&apos;ll notify you that we&apos;ve
                received it. Then we&apos;ll inspect it and decide whether we
                will repair it, replace it or refund your money.
            </p>
            <p>
                We will immediately notify you on the status of your returned
                item after inspecting it.
            </p>
            <p>
                If a return is approved, we will initiate a refund to your
                credit card (or original method of payment).
            </p>
            <p>
                You will receive the credit within a certain amount of days,
                depending on your card issuer&apos;s policies.
            </p>
            <p className={css(S.BoldPolicyContent)}>Shipping Costs</p>
            <p>
                If your product is faulty or doesn’t match its description, you
                are entitled to have the cost of return postage refunded to you.
            </p>
            <p>
                Please be aware, for some products, the original shipping and
                return shipping costs may not be refundable.
            </p>
            <p>
                You are responsible for the return shipping cost for change of
                mind purchases that are accepted for return.
            </p>
            <h4 className={css(S.PolicyContact)}>Contact Us</h4>
            <p>
                If you have any questions on how to return your item to us,
                contact us via our <a href={contactURL}>contact page</a>.
            </p>
        </div>
    );
};

export default ReturnsPolicyTemplate;
